import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from '../ui/Dialog.jsx';
import { Button } from '../ui/Button.jsx';
import { TextArea } from '../ui/TextArea.jsx';
import { Select } from '../ui/Select.jsx';
import { Alert } from '../ui/Alert.jsx';
import { formatSourceReference } from '../chat/trust';

/** Mission forms offered in the preview. Difficulty feeds `POST /api/missions`. */
export const MISSION_FORMS = [
  { id: 'standard', labelKey: 'etchPreview.missionFormStandard', difficulty: 2 },
  { id: 'scenario', labelKey: 'etchPreview.missionFormScenario', difficulty: 3 },
  { id: 'definition', labelKey: 'etchPreview.missionFormDefinition', difficulty: 1 },
];

const MAX_STATEMENT = 600;
const MAX_TITLE = 120;

/**
 * First sentence(s) of an answer, used as the proposed "Kernaussage".
 * Markdown decoration and citation markers are stripped — the learner should
 * see the claim, not the formatting of the chat message it came from.
 */
export function proposeStatement(text = '') {
  const plain = String(text || '')
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/\[s\d+\]/gi, '')
    .replace(/[*_`>#]/g, '')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
  if (!plain) return '';
  const sentences = plain.match(/[^.!?]+[.!?]?/g) || [plain];
  let out = '';
  for (const sentence of sentences) {
    if (out && (out + sentence).length > 280) break;
    out += sentence;
    if (out.length > 160) break;
  }
  return out.trim().slice(0, MAX_STATEMENT);
}

/**
 * Words that carry no meaning on their own and must never end up as a graded
 * key term. Kept deliberately small — the goal is to drop filler, not to build
 * a linguistic model.
 */
const STOPWORDS = new Set([
  'aber', 'alle', 'allen', 'als', 'also', 'andere', 'auch', 'auf', 'aus', 'bei', 'beim', 'bin', 'bis',
  'dabei', 'damit', 'dann', 'das', 'dass', 'dem', 'den', 'der', 'des', 'die', 'dies', 'diese', 'diesem',
  'diesen', 'dieser', 'dieses', 'doch', 'dort', 'durch', 'ein', 'eine', 'einem', 'einen', 'einer',
  'eines', 'etwa', 'für', 'gegen', 'hat', 'hatte', 'ihr', 'ihre', 'immer', 'ist', 'kann', 'man', 'mehr',
  'mit', 'muss', 'nach', 'nicht', 'noch', 'nur', 'oder', 'ohne', 'sein', 'seine', 'sich', 'sie', 'sind',
  'soll', 'sollte', 'über', 'und', 'unter', 'vom', 'von', 'vor', 'war', 'was', 'wenn', 'werden', 'wie',
  'wird', 'wir', 'zum', 'zur', 'zwischen',
  'about', 'after', 'all', 'also', 'and', 'any', 'are', 'been', 'but', 'can', 'for', 'from', 'has',
  'have', 'how', 'into', 'its', 'may', 'more', 'must', 'not', 'only', 'other', 'over', 'should', 'such',
  'than', 'that', 'the', 'their', 'them', 'then', 'there', 'these', 'they', 'this', 'those', 'through',
  'was', 'were', 'when', 'which', 'while', 'will', 'with', 'would', 'you', 'your',
]);

const MAX_KEYWORDS = 5;

/**
 * The decisive terms of a Kernaussage.
 *
 * `scoreTextAnswer` (src/learning/mission-engine.js) falls back to *every*
 * distinct word of `expected` when `keywords` is empty, which makes a correct
 * paraphrase fail and a verbatim copy the only way to pass. Shipping a short,
 * decisive term list instead keeps free recall gradeable without turning the
 * mission into a copy-paste test.
 *
 * @param {string} text
 * @returns {string[]} at most {@link MAX_KEYWORDS} distinct terms
 */
export function deriveKeywords(text = '') {
  const tokens = String(text || '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .split(/\s+/)
    .filter((token) => token.length > 3 && !STOPWORDS.has(token));

  const counts = new Map();
  for (const token of tokens) counts.set(token, (counts.get(token) || 0) + 1);

  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || b[0].length - a[0].length)
    .slice(0, MAX_KEYWORDS)
    .map(([token]) => token);
}

/**
 * The editable draft the panel starts from. Nothing here is persisted — the
 * draft only becomes a mission after the user confirms (spec §9.2 step 4).
 */
export function buildEtchDraft({ statement = '', goal = '', sourceId = null, form = 'standard', topic = null } = {}) {
  return { statement, goal, sourceId, form, topic };
}

/**
 * Translates the confirmed draft into the `POST /api/missions` body
 * (steps shape per WAVE1-INTERFACES §4).
 *
 * @param {object} draft            the confirmed draft
 * @param {object} ctx
 * @param {(key: string, opts?: object) => string} ctx.t
 * @param {object[]} [ctx.sources]  resolved source descriptors
 * @param {string|null} [ctx.workspacePath]
 */
export function buildMissionPayload(draft, { t, sources = [], workspacePath = null } = {}) {
  const statement = String(draft?.statement || '').trim();
  const goal = String(draft?.goal || '').trim() || t('etchPreview.defaultGoal');
  const source = sources.find((s) => s.id === draft?.sourceId) || null;
  const form = MISSION_FORMS.find((f) => f.id === draft?.form) || MISSION_FORMS[0];

  const citeOptions = [
    source
      ? { id: 'o1', label: formatSourceReference(source) || source.title || source.ref, correct: true, ref: source.ref || null }
      : { id: 'o1', label: t('etchPreview.citeOptionModel'), correct: true, ref: null },
    source
      ? { id: 'o2', label: t('etchPreview.citeOptionModel'), correct: false, ref: null }
      : { id: 'o2', label: t('etchPreview.citeOptionNone'), correct: false, ref: null },
    { id: 'o3', label: t('etchPreview.citeOptionUnrelated'), correct: false, ref: null },
  ];

  const scenario = form.id === 'scenario' ? t('etchPreview.applyScenario') : null;
  const keywords = deriveKeywords(statement);

  return {
    title: statement.slice(0, MAX_TITLE),
    origin: 'einpraegen',
    topic: draft?.topic || source?.title || null,
    difficulty: form.difficulty,
    scenario,
    workspacePath: workspacePath || null,
    sourceRef: source
      ? { id: source.id, kind: source.kind, title: source.title, ref: source.ref, locator: source.locator }
      : null,
    steps: {
      recall: {
        prompt: goal,
        expected: statement,
        keywords,
        hint: t('etchPreview.recallHint'),
      },
      // Two differently framed situations, always. A single variant would make
      // `isTransferAttempt` false forever, and mastery could never reach
      // 'secure' (spec §2.3) — transfer needs a scenario the learner has not
      // worked before.
      apply: {
        prompt: t('etchPreview.applyPrompt'),
        variants: [
          {
            id: 'v1',
            scenario: scenario || t('etchPreview.applyScenario'),
            prompt: t('etchPreview.applyVariantPrompt'),
            expected: statement,
            keywords,
          },
          {
            id: 'v2',
            scenario: t('etchPreview.applyScenarioTransfer'),
            prompt: t('etchPreview.applyVariantPromptTransfer'),
            expected: statement,
            keywords,
          },
        ],
      },
      cite: {
        prompt: t('etchPreview.citePrompt'),
        options: citeOptions,
      },
      confidence: {
        prompt: t('etchPreview.confidencePrompt'),
        scaleMax: 5,
      },
      feedback: {
        explanation: statement,
        nextAction: 'revisit_later',
      },
    },
  };
}

/**
 * The Einprägen preview (design spec §9.2 step 2).
 *
 * Shows Kernaussage, Lernziel, Quelle, vorgeschlagene Missionsform and the
 * erwartete erste Wiederholung. The first three and the mission form are
 * editable; the revisit cadence is derived by the mastery engine (a brand new
 * mission is due immediately) and is therefore shown, not faked as an input.
 *
 * The component is pure UI: it never writes anything. `onConfirm(draft)` is
 * what turns the draft into a mission, and only the parent does that.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {object} props.draft
 * @param {(draft: object) => void} props.onDraftChange
 * @param {(draft: object) => void} props.onConfirm
 * @param {object[]} [props.sources] - selectable sources for this answer.
 * @param {boolean} [props.saving]
 * @param {string|null} [props.error]
 */
export function EtchPreview({
  open,
  onClose,
  draft,
  onDraftChange,
  onConfirm,
  sources = [],
  saving = false,
  error = null,
}) {
  const { t } = useTranslation();
  const statementRef = useRef(null);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!open) setTouched(false);
  }, [open]);

  const statement = draft?.statement ?? '';
  const invalid = touched && !statement.trim();

  const sourceOptions = useMemo(
    () => [
      { value: '', label: t('etchPreview.sourceNone') },
      ...sources.map((source) => ({
        value: source.id,
        label: source.state === 'unreachable'
          ? `${source.title || source.id} — ${t('states.noSource')}`
          : formatSourceReference(source) || source.title || source.id,
      })),
    ],
    [sources, t]
  );

  const selectedSource = sources.find((s) => s.id === draft?.sourceId) || null;

  const update = (patch) => onDraftChange?.({ ...draft, ...patch });

  const submit = (event) => {
    event?.preventDefault?.();
    setTouched(true);
    if (!statement.trim()) {
      statementRef.current?.focus();
      return;
    }
    onConfirm?.(draft);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('etchPreview.title')}
      description={t('etchPreview.editHint')}
      closeLabel={t('a11y.closeDialog')}
      initialFocusRef={statementRef}
      className="etch-preview"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            {t('etchPreview.cancel')}
          </Button>
          <Button variant="primary" onClick={submit} loading={saving}>
            {t('etchPreview.save')}
          </Button>
        </>
      }
    >
      <form className="etch-preview__form" onSubmit={submit}>
        {error && (
          <Alert tone="danger" title={t('states.error')}>
            {error}
          </Alert>
        )}

        <TextArea
          ref={statementRef}
          label={t('etchPreview.keyStatement')}
          value={statement}
          rows={4}
          maxLength={MAX_STATEMENT}
          required
          error={invalid ? t('etchPreview.statementRequired') : undefined}
          onChange={(event) => update({ statement: event.target.value })}
        />

        <TextArea
          label={t('etchPreview.learningGoal')}
          value={draft?.goal ?? ''}
          rows={2}
          hint={t('etchPreview.learningGoalHint')}
          onChange={(event) => update({ goal: event.target.value })}
        />

        <Select
          label={t('etchPreview.source')}
          value={draft?.sourceId ?? ''}
          options={sourceOptions}
          onChange={(event) => update({ sourceId: event.target.value || null })}
        />

        {selectedSource?.locator && (
          <p className="etch-preview__locator">{selectedSource.locator}</p>
        )}

        <Select
          label={t('etchPreview.missionType')}
          value={draft?.form ?? 'standard'}
          options={MISSION_FORMS.map((form) => ({ value: form.id, label: t(form.labelKey) }))}
          onChange={(event) => update({ form: event.target.value })}
        />

        <div className="etch-preview__review">
          <span className="etch-preview__review-label">{t('etchPreview.firstReview')}</span>
          <span className="etch-preview__review-value">{t('etchPreview.firstReviewImmediate')}</span>
          <span className="etch-preview__review-hint">{t('etchPreview.firstReviewHint')}</span>
        </div>
      </form>
    </Dialog>
  );
}

export default EtchPreview;
